import { useState, useEffect } from 'react';
import { Search, Percent, ChevronLeft, ChevronRight, Check } from 'lucide-react';

interface ProviderCommission {
  id: string;
  email: string;
  company_name: string | null;
  ceo_name: string | null;
  business_category: string | null;
  business_approved: boolean;
  commission_rate: number;
  commission_updated_at: string | null;
  created_at: string;
}

export default function AdminProviderCommissionTab() {
  const [providers, setProviders] = useState<ProviderCommission[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [activeSearch, setActiveSearch] = useState('');
  const [message, setMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);
  const [savingId, setSavingId] = useState<string | null>(null);

  // 행별 편집 중인 입력값 (문자열로 보관)
  const [drafts, setDrafts] = useState<Record<string, string>>({});

  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const pageSize = 10;

  const fetchProviders = async () => {
    setLoading(true);
    setError(null);
    try {
      const params = new URLSearchParams({
        page: page.toString(),
        limit: pageSize.toString(),
        ...(activeSearch && { search: activeSearch }),
      });
      const response = await fetch(`/api/v1/admin/provider-commission?${params}`, {
        credentials: 'include',
      });
      if (!response.ok) {
        throw new Error('수수료율 목록을 불러오는데 실패했습니다.');
      }
      const data = await response.json();
      const list: ProviderCommission[] = data.providers || [];
      setProviders(list);
      setTotalPages(data.totalPages || 1);
      setTotalCount(data.totalCount || 0);
      // 드래프트 초기화
      const initial: Record<string, string> = {};
      list.forEach((p) => {
        initial[p.id] = String(p.commission_rate);
      });
      setDrafts(initial);
    } catch (err) {
      setError(err instanceof Error ? err.message : '오류가 발생했습니다.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchProviders();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, activeSearch]);

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    setPage(1);
    setActiveSearch(searchTerm);
  };

  const handleShowAll = () => {
    setSearchTerm('');
    setActiveSearch('');
    setPage(1);
  };

  const isDirty = (p: ProviderCommission) => {
    const draft = drafts[p.id];
    if (draft === undefined) return false;
    const num = Number(draft);
    if (draft.trim() === '' || Number.isNaN(num)) return true; // 유효성은 저장 시 검증
    return num !== p.commission_rate;
  };

  const handleSave = async (p: ProviderCommission) => {
    const draft = drafts[p.id];
    const rate = Number(draft);
    if (draft === undefined || draft.trim() === '' || Number.isNaN(rate) || rate < 0 || rate > 100) {
      setMessage({ type: 'error', text: '수수료율은 0~100 사이의 숫자여야 합니다.' });
      return;
    }
    setSavingId(p.id);
    setMessage(null);
    try {
      const response = await fetch(`/api/v1/admin/provider-commission/${p.id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ rate }),
      });
      const data = await response.json();
      if (!response.ok) {
        throw new Error(data.error || '수수료율 저장에 실패했습니다.');
      }
      setMessage({
        type: 'success',
        text: `${p.company_name || p.email} — 수수료율 ${rate}%로 저장되었습니다.`,
      });
      await fetchProviders();
    } catch (err) {
      setMessage({
        type: 'error',
        text: err instanceof Error ? err.message : '오류가 발생했습니다.',
      });
    } finally {
      setSavingId(null);
    }
  };

  const formatDate = (dateString: string | null) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleDateString('ko-KR', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });
  };

  return (
    <div>
      {/* 헤더 */}
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
          <Percent className="w-6 h-6 text-accent" />
          교육 기관 중개수수료율 설정
        </h1>
        <p className="text-gray-500 mt-1">
          전체 {totalCount}개 교육 기관 — 수업 결제 시 적용될 기관별 중개수수료율을 설정합니다. (기본 1%)
        </p>
      </div>

      {/* 메시지 */}
      {message && (
        <div
          className={`mb-4 p-4 rounded-lg ${
            message.type === 'success' ? 'bg-green-50 text-green-700' : 'bg-red-50 text-red-700'
          }`}
        >
          {message.text}
        </div>
      )}

      {/* 검색 */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-4 mb-4 flex flex-wrap items-center gap-3">
        <form onSubmit={handleSearch} className="flex-1 min-w-[200px] flex gap-2">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
            <input
              type="text"
              placeholder="업체명·대표자·이메일 검색"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-9 pr-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-primary focus:border-transparent"
            />
          </div>
          <button
            type="submit"
            className="px-4 py-2 bg-primary text-white rounded-lg text-sm font-medium hover:bg-primary/90"
          >
            검색
          </button>
          {activeSearch && (
            <button
              type="button"
              onClick={handleShowAll}
              className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg text-sm font-medium hover:bg-gray-200"
            >
              전체 보기
            </button>
          )}
        </form>
      </div>

      {/* 본문 */}
      {loading ? (
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-12 text-center text-gray-500">
          불러오는 중...
        </div>
      ) : error ? (
        <div className="bg-red-50 text-red-700 rounded-xl border border-red-200 p-6">{error}</div>
      ) : providers.length === 0 ? (
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-12 text-center text-gray-500">
          조건에 맞는 교육 기관이 없습니다.
        </div>
      ) : (
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-gray-50 text-gray-600">
                <tr>
                  <th className="px-4 py-3 text-left font-medium">업체명</th>
                  <th className="px-4 py-3 text-left font-medium">대표자</th>
                  <th className="px-4 py-3 text-left font-medium">이메일</th>
                  <th className="px-4 py-3 text-center font-medium">승인</th>
                  <th className="px-4 py-3 text-center font-medium">수수료율 (%)</th>
                  <th className="px-4 py-3 text-left font-medium">최종 수정일</th>
                  <th className="px-4 py-3 text-center font-medium">저장</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {providers.map((p) => {
                  const dirty = isDirty(p);
                  return (
                    <tr key={p.id} className="hover:bg-gray-50">
                      <td className="px-4 py-3 font-medium text-gray-900">
                        {p.company_name || '-'}
                      </td>
                      <td className="px-4 py-3 text-gray-700">{p.ceo_name || '-'}</td>
                      <td className="px-4 py-3 text-gray-700">{p.email}</td>
                      <td className="px-4 py-3 text-center">
                        {p.business_approved ? (
                          <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-700">
                            승인
                          </span>
                        ) : (
                          <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-500">
                            대기
                          </span>
                        )}
                      </td>
                      <td className="px-4 py-3 text-center">
                        <div className="inline-flex items-center gap-1">
                          <input
                            type="number"
                            step="0.1"
                            min="0"
                            max="100"
                            value={drafts[p.id] ?? ''}
                            onChange={(e) =>
                              setDrafts((prev) => ({ ...prev, [p.id]: e.target.value }))
                            }
                            onKeyDown={(e) => {
                              if (e.key === 'Enter' && dirty) handleSave(p);
                            }}
                            className={`w-20 px-2 py-1.5 border rounded-lg text-sm text-right focus:ring-2 focus:ring-primary focus:border-transparent ${
                              dirty ? 'border-accent bg-orange-50' : 'border-gray-300'
                            }`}
                          />
                          <span className="text-gray-400">%</span>
                        </div>
                      </td>
                      <td className="px-4 py-3 text-gray-500">
                        {formatDate(p.commission_updated_at)}
                      </td>
                      <td className="px-4 py-3 text-center">
                        <button
                          onClick={() => handleSave(p)}
                          disabled={!dirty || savingId === p.id}
                          className="inline-flex items-center gap-1 px-3 py-1.5 rounded-lg text-sm font-medium text-white bg-primary hover:bg-primary/90 disabled:opacity-40 disabled:cursor-not-allowed"
                        >
                          <Check className="w-3.5 h-3.5" />
                          {savingId === p.id ? '저장 중' : '저장'}
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          {/* 페이지네이션 */}
          {totalPages > 1 && (
            <div className="flex items-center justify-between px-4 py-3 border-t border-gray-100">
              <span className="text-sm text-gray-500">
                {page} / {totalPages} 페이지 (총 {totalCount}개)
              </span>
              <div className="flex gap-1">
                <button
                  onClick={() => setPage((p) => Math.max(1, p - 1))}
                  disabled={page === 1}
                  className="p-1.5 rounded-md border border-gray-200 text-gray-600 disabled:opacity-40 hover:bg-gray-100"
                >
                  <ChevronLeft className="w-4 h-4" />
                </button>
                <button
                  onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                  disabled={page === totalPages}
                  className="p-1.5 rounded-md border border-gray-200 text-gray-600 disabled:opacity-40 hover:bg-gray-100"
                >
                  <ChevronRight className="w-4 h-4" />
                </button>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
